import { Modal } from "../components";
import FileService from "../services/FileService";
const allowedFileTypes = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'video/mp4'
]);

const allowedFileExtensions = new Set([
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff',
  'mp4'
]);

interface BlobSasResponse {
  uploadUrl: string;
  blobUrl: string;
  fileName: string;
}

function generateGuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    (character) => {
      const randomValue = Math.floor(Math.random() * 16);
      const value =
        character === 'x'
          ? randomValue
          : (randomValue & 0x3) | 0x8;

      return value.toString(16);
    }
  );
}

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return '';
  }

  return fileName
    .slice(lastDotIndex + 1)
    .toLowerCase();
}

function getFileNameWithoutExtension(fileName: string): string {
  const extension = getFileExtension(fileName);

  if (!extension) {
    return fileName;
  }

  return fileName.slice(0, -(extension.length + 1));
}

function sanitizeFileName(fileName: string): string {
  return fileName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function isPdfOrImage(file: File): boolean {
  const extension = getFileExtension(file.name);
  const contentType = file.type.toLowerCase();

  return (
    allowedFileTypes.has(contentType) ||
    allowedFileExtensions.has(extension)
  );
}

function createRenamedFileName(file: File): string {
  const extension = getFileExtension(file.name);
  const originalName = getFileNameWithoutExtension(file.name);

  const sanitizedName =
    sanitizeFileName(originalName) || 'arquivo';

  return `${sanitizedName}-${generateGuid()}${
    extension ? `.${extension}` : ''
  }`;
}

export async function uploadFiles(input: HTMLInputElement,directorySave: string): Promise<string[]> {
  const files = input?.files ? Array.from(input.files) : [];

  if (files.length === 0) {
    throw new Error('Selecione pelo menos um arquivo.');
  }

  const invalidFile = files.find((file) => !isPdfOrImage(file));

  if (invalidFile) {
    throw new Error(`O arquivo ${invalidFile.name} deve ser um PDF ou uma imagem.`);
  }

  const savedFileUrls: string[] = [];

  for (const file of files) {
    const renamedFileName = createRenamedFileName(file);

    const formSend = {fileName: renamedFileName,directorySave};

    try {
      const response: BlobSasResponse = await FileService.getBlobSas(formSend);

      if (!response?.uploadUrl || !response?.blobUrl) {
        throw new Error('Falha ao obter a URL de upload do Azure Blob.');
      }

      const blobUrl = await uploadFile(file, response);

      savedFileUrls.push(blobUrl);
    } catch (error: any) {
      Modal.show({
        title: 'Erro ao salvar arquivo',
        body: Modal.getErrorMessage(error, formSend),
        showCancel: true,
        showConfirm: false,
        onCancelText: 'Fechar'
      });

      throw error;
    }
  }

  return savedFileUrls;
}

async function uploadFile(file: any, sas: any): Promise<string> {
  const uploadResponse = await fetch(sas.uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type':
        file.type || 'application/octet-stream'
    },
    body: file
  });

  if (!uploadResponse.ok) {
    throw new Error(`Erro ao enviar ${file.name}.`);
  }
  return sas.blobUrl;
}

const HelperFile = {
  uploadFiles
};

export default HelperFile;